export default {
    props: {
        submitted: {type: Boolean},
        errors: {type: Object, default: () => ({})},
    },

    data() {
        return {
            isBooting: false,
            isLoading: false,
            isSubmitting: false,
            isSubmitted: false,

            formData: {},
            invalidFields: {},
            serverErrors: {},
        };
    },

    computed: {
        isValid() {
            return Object.keys(this.invalidFields).length === 0;
        },

        isDisabled() {
            return this.isBooting || this.isLoading || this.isSubmitting;
        },

        formFields() {
            return Object.keys(this.$options.formFields);
        },
    },

    beforeCreate() {
        if (!this.$options.formFields) {
            throw Error('[FormMixin] formFields options is not defined');
        }

        const formFields = Object.keys(this.$options.formFields);

        for (let field of formFields) {
            this.$options.computed[field] = {
                get() {
                    return this.formData[field];
                },
                set(value) {
                    this.hydrateField(field, value);
                },
            };
        }
    },

    // created() // bugs ios 12.2+
    mounted() {
        this.boot();
    },

    watch: {
        submitted: {
            immediate: true,
            handler(submitted) {
                this.isSubmitted = this.isSubmitted || (submitted === true);
            },
        },

        errors: {
            immediate: true,
            handler(errors) {
                if (!errors) {
                    return;
                }

                if (errors.errors) {
                    this.handleServerErrors(errors.errors);
                } else {
                    this.handleServerErrors(errors);
                }
            },
        },

        isValid: {
            immediate: true,

            handler(isValid) {
                if (isValid) {
                    this.$emit('valid');
                } else {
                    this.$emit('invalid');
                }
            },
        },
    },

    methods: {
        bootHook() {
            // STUB - overwrite it
            return Promise.resolve(null);
        },

        // eslint-disable-next-line no-unused-vars
        bootedHook(response) {
            // STUB - overwrite it
            return Promise.resolve(null);
        },

        // eslint-disable-next-line no-unused-vars
        bootFailedHook(reason) {
            // STUB - overwrite it
            // console.error( reason );
        },

        loadHook() {
            // STUB - overwrite it
            return Promise.resolve(null);
        },

        // eslint-disable-next-line no-unused-vars
        loadedHook(model) {
            // STUB - overwrite it
            return Promise.resolve(null);
        },

        // eslint-disable-next-line no-unused-vars
        loadFailedHook(reason) {
            // STUB - overwrite it
            // console.error( reason );
        },

        submitHook() {
            // STUB - overwrite it
            return Promise.reject(false);
        },

        // eslint-disable-next-line no-unused-vars
        submittedHook(response) {
            // STUB - overwrite it
            // return true to keep isSubmitting = true, useful when redirecting after submit
            this.reset();
        },

        // eslint-disable-next-line no-unused-vars
        submitFailedHook(reason) {
            // STUB - overwrite it
            // console.error( reason );
        },

        invalidHook() {
            // STUB - overwrite it
            // notify user of ivalid form data
        },

        boot() {
            this.reset(false);
            this.isBooting = true;

            return Promise.resolve(this.bootHook())
                .then(() => this.load())
                .then((response) => {
                    this.isBooting = false;

                    return new Promise((resolve) => {
                        this.$nextTick(() => {
                            resolve(this.bootedHook(response));
                        });
                    });
                })
                .catch((reason) => {
                    this.isBooting = false;

                    return new Promise((resolve, reject) => {
                        this.$nextTick(() => {
                            reject(this.bootFailedHook(reason));
                        });
                    });
                });
        },

        load() {
            this.isLoading = true;

            return Promise.resolve(this.loadHook())
                .then((model) => {
                    this.hydrate(model);

                    return new Promise((resolve) => {
                        this.$nextTick(() => {
                            resolve(this.loadedHook(model));
                        });
                    });
                })
                .then((response) => {
                    this.isLoading = false;

                    return response;
                })
                .catch((reason) => {
                    this.isLoading = false;

                    return new Promise((resolve, reject) => {
                        this.$nextTick(() => {
                            reject(this.loadFailedHook(reason));
                        });
                    });
                });
        },

        submit() {
            this.setSubmitted();

            if (!this.isValid) {
                return this.invalidHook();
            }

            this.isSubmitting = true;

            return Promise.resolve(this.submitHook())
                .then((response) => new Promise((resolve) => {
                    this.$nextTick(() => {
                        resolve(this.submittedHook(response));
                    });
                }))
                .then((keepSubmitting) => {
                    this.isSubmitting = keepSubmitting === true;

                    if (!this.isSubmitting) {
                        this.$nextTick(() => {
                            this.reset(false);
                        });
                    }
                })
                .catch((reason) => {
                    this.isSubmitting = false;
                    this.handleServerErrors(reason.errors);

                    return new Promise((resolve, reject) => {
                        this.$nextTick(() => {
                            reject(this.submitFailedHook(reason));
                        });
                    });
                });
        },

        reset(autofocus = true) {
            this.isBooting = false;
            this.isLoading = false;
            this.isSubmitting = false;

            this.isSubmitted = false;

            this.hydrate(null);
            this.handleServerErrors(null);

            if (autofocus === true) {
                this.formAutofocus();
            }
        },

        setSubmitted() {
            this.isSubmitted = true;
        },

        resetSubmittingState() {
            this.isSubmitting = false;
        },

        formAutofocus() {
            setTimeout(() => {
                this.$refs.autofocus && this.$refs.autofocus.focus();
            }, 100);
        },

        hydrateField(field, value) {
            const emptyValue = (this.formFields.indexOf(field) > -1) ? null : undefined;

            if ((value === null) || (value === undefined)) {
                this.$set(this.formData, field, emptyValue);
            } else {
                this.$set(this.formData, field, value);
            }

            this.$delete(this.serverErrors, field);
        },

        hydrate(model) {
            for (let field of this.formFields) {
                const value = model && model[field];

                this.hydrateField(field, value);
            }
        },

        handleServerErrors(errors) {
            for (let field of this.formFields) {
                if (errors && errors[field]) {
                    this.$set(this.serverErrors, field, errors[field]);
                } else {
                    this.$delete(this.serverErrors, field);
                }
            }
        },

        handleValidation(field, isValid) {
            if (isValid === true) {
                this.$delete(this.invalidFields, field);
            } else {
                this.$set(this.invalidFields, field, true);
            }
        },
    },
};
